/**
 * Stake/territory logic: removal, ownership, scoring, detection.
 * Pure functions — no side effects, no rendering.
 */

import type {
  PlayerState,
  PlanetState,
  PlantedFlagState,
  GameState,
  ModeConfig,
  Vec3,
} from '../types';
import { STAKE_REMOVE_MOVEMENT_COST, STAKE_REMOVE_RANGE } from '../constants';
import { vecNormalize, vecAdd, vecScale, vecSub, vecLength, vecDot } from '../math';
import { getModeRules } from './modeRules';
import { dacos } from './dmath';

// =============================================================================
// TYPES
// =============================================================================

export interface TerritoryScore {
  planetIndex: number;
  owningTeam: number | null;  // null = contested/unowned
  contested: boolean;
  stakeCounts: Map<number, number>;
}

// =============================================================================
// QUERY HELPERS
// =============================================================================

/** Get alive stakes on a given planet. */
export function getStakesOnPlanet(
  flags: PlantedFlagState[],
  planetIndex: number
): PlantedFlagState[] {
  return flags.filter(f => f.planetIndex === planetIndex && f.alive !== false);
}

/** Count alive stakes per team on a given planet. */
export function getStakeCountPerTeam(
  flags: PlantedFlagState[],
  planetIndex: number
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const f of flags) {
    if (f.planetIndex !== planetIndex || f.alive === false) continue;
    counts.set(f.teamIndex, (counts.get(f.teamIndex) ?? 0) + 1);
  }
  return counts;
}

// =============================================================================
// STAKE REMOVAL
// =============================================================================

/** Check if a player can remove a specific stake. */
export function canRemoveStake(
  player: PlayerState,
  stake: PlantedFlagState,
  planets: PlanetState[],
  removeCost?: number
): boolean {
  const cost = removeCost ?? STAKE_REMOVE_MOVEMENT_COST;

  // Must be alive, on ground, have enough movement
  if (!player.alive || player.airborne) return false;
  if (player.movementRemaining < cost) return false;

  // Must be an enemy stake
  if (stake.teamIndex === player.teamIndex) return false;

  // Stake must be alive
  if (stake.alive === false) return false;

  // Must be on the same planet
  if (stake.planetIndex !== player.planetIndex) return false;

  // Must be within range on planet surface
  const planet = planets[player.planetIndex];
  const dist = arcDistanceOnPlanet(player.surfaceDir, stake.surfaceDir, planet.radius);
  return dist <= STAKE_REMOVE_RANGE;
}

/** Get all stakes the player can currently remove. */
export function getRemovableStakes(
  player: PlayerState,
  flags: PlantedFlagState[],
  planets: PlanetState[],
  removeCost?: number
): PlantedFlagState[] {
  return flags.filter(f => canRemoveStake(player, f, planets, removeCost));
}

/** Remove a stake: mark alive=false, deduct movement. Returns updated state. */
export function removeStake(
  state: GameState,
  playerId: number,
  stakeId: number
): GameState {
  const playerIndex = state.players.findIndex(p => p.id === playerId);
  if (playerIndex < 0) return state;

  const player = state.players[playerIndex];
  const stake = state.plantedFlags.find(f => f.id === stakeId);
  if (!stake) return state;

  const cost = state.config.modeConfig.stakeRemoveCost ?? STAKE_REMOVE_MOVEMENT_COST;
  if (!canRemoveStake(player, stake, state.planets, cost)) return state;

  const newFlags = state.plantedFlags.map(f =>
    f.id === stakeId ? { ...f, alive: false } : f
  );

  const newPlayers = state.players.map((p, i) =>
    i === playerIndex
      ? { ...p, movementRemaining: p.movementRemaining - cost }
      : p
  );

  return { ...state, players: newPlayers, plantedFlags: newFlags };
}

// =============================================================================
// PLANTING ENFORCEMENT
// =============================================================================

/** Check if a player can plant a stake on a given planet (mode rules). */
export function canPlantOnPlanet(
  player: PlayerState,
  planetIndex: number,
  flags: PlantedFlagState[],
  modeConfig: ModeConfig
): boolean {
  // Stakes only constrain each other when territory is the objective
  if (getModeRules(modeConfig).objective !== 'stakes') return true;

  // Multi-stake modes: always allowed
  if (modeConfig.multiStake) return true;

  // Single-stake modes (sovereignty): blocked if enemy has alive stake on planet
  const stakesOnPlanet = getStakesOnPlanet(flags, planetIndex);
  const hasEnemyStake = stakesOnPlanet.some(f => f.teamIndex !== player.teamIndex);
  return !hasEnemyStake;
}

// =============================================================================
// TERRITORY OWNERSHIP & SCORING
// =============================================================================

/** Compute ownership for each planet that has stakes. */
export function computePlanetOwnership(
  flags: PlantedFlagState[],
  planets: PlanetState[]
): TerritoryScore[] {
  const scores: TerritoryScore[] = [];

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    // Skip non-planet bodies
    if (planet.type !== 'planet' || !planet.alive) continue;

    const stakeCounts = getStakeCountPerTeam(flags, pi);
    if (stakeCounts.size === 0) {
      scores.push({ planetIndex: pi, owningTeam: null, contested: false, stakeCounts });
      continue;
    }

    // Find the team(s) with the most stakes
    let maxCount = 0;
    for (const count of stakeCounts.values()) {
      if (count > maxCount) maxCount = count;
    }

    const topTeams: number[] = [];
    for (const [teamIdx, count] of stakeCounts) {
      if (count === maxCount) topTeams.push(teamIdx);
    }

    if (topTeams.length === 1) {
      scores.push({ planetIndex: pi, owningTeam: topTeams[0], contested: false, stakeCounts });
    } else {
      scores.push({ planetIndex: pi, owningTeam: null, contested: true, stakeCounts });
    }
  }

  return scores;
}

/** Count owned planets per team. */
export function computeTerritoryScores(state: GameState): Map<number, number> {
  const ownership = computePlanetOwnership(state.plantedFlags, state.planets);
  const scores = new Map<number, number>();

  // Initialize all teams to 0
  for (const p of state.players) {
    if (!scores.has(p.teamIndex)) scores.set(p.teamIndex, 0);
  }

  for (const o of ownership) {
    if (o.owningTeam != null) {
      scores.set(o.owningTeam, (scores.get(o.owningTeam) ?? 0) + 1);
    }
  }

  return scores;
}

// =============================================================================
// HIDDEN STAKE DETECTION
// =============================================================================

/** Check if a player can detect a hidden stake (world-space distance). */
export function canDetectStake(
  player: PlayerState,
  stake: PlantedFlagState,
  planets: PlanetState[],
  scanRange: number
): boolean {
  // Own team stakes are always detected
  if (stake.teamIndex === player.teamIndex) return false; // not "detected" — just visible
  if (stake.alive === false) return false;

  const playerPos = getWorldPos(player.surfaceDir, planets[player.planetIndex]);
  const stakePos = getWorldPos(stake.surfaceDir, planets[stake.planetIndex]);
  const dist = vecLength(vecSub(playerPos, stakePos));

  return dist <= scanRange;
}

/** Find all hidden enemy stakes the player can detect. */
export function detectHiddenStakes(
  player: PlayerState,
  flags: PlantedFlagState[],
  planets: PlanetState[],
  scanRange: number
): PlantedFlagState[] {
  return flags.filter(f =>
    f.teamIndex !== player.teamIndex &&
    f.alive !== false &&
    canDetectStake(player, f, planets, scanRange)
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function arcDistanceOnPlanet(dir1: Vec3, dir2: Vec3, radius: number): number {
  const d1 = vecNormalize(dir1);
  const d2 = vecNormalize(dir2);
  const d = vecDot(d1, d2);
  const angle = dacos(Math.max(-1, Math.min(1, d)));
  return angle * radius;
}

function getWorldPos(surfaceDir: Vec3, planet: PlanetState): Vec3 {
  const normal = vecNormalize(surfaceDir);
  return vecAdd(planet.position, vecScale(normal, planet.radius));
}
